import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { message } from "antd";
import {
  updateDoctor,
  getCurrentDoctor,
  sanitizeDoctorUpdateValues,
} from "../../authDoctor/redux/thunks/doctorsThunks";

const BASE_FILES_URL = (() => {
  const v = import.meta?.env?.VITE_REGISTER_DOCTOR_URL;
  // fallback DEV only (évite un null si env manquant)
  return v && String(v).trim()
    ? String(v).trim().replace(/\/+$/, "") // remove trailing slashes
    : "https://back.mediwave.tn"; // default fallback (DEV only)
})();

/* ============================================================
   🔹 AVATAR RESOLVER (PUR – utilisable partout)
   ============================================================ */
/**
 * Convertit un avatar brut (URL, path Windows, path relatif) en URL affichable.
 * Supporte:
 * - http(s)://...
 * - C:\...\uploads\doctors\img.jpg
 * - /uploads/doctors/img.jpg
 * - uploads/doctors/img.jpg
 */
export const resolveDoctorAvatar = (rawAvatar) => {
  if (!rawAvatar) return null;

  const raw = String(rawAvatar).trim();
  if (!raw || raw.toLowerCase() === "undefined" || raw.toLowerCase() === "null")
    return null;

  // 1) Déjà une URL complète http(s)
  if (/^https?:\/\//i.test(raw)) return raw;

  // 2) Normaliser path Windows -> URL path
  const normalized = raw.replaceAll("\\", "/");
  const lower = normalized.toLowerCase();

  // 3) Trouver "uploads/" (avec ou sans slash avant)
  let idx = lower.indexOf("/uploads/");
  if (idx === -1) idx = lower.indexOf("uploads/");

  if (idx === -1) {
    return normalized.startsWith("/") ? `${BASE_FILES_URL}${normalized}` : null;
  }

  // 4) extraire le chemin relatif
  let relative = normalized.slice(idx);

  // assurer que le relative commence par "/"
  if (!relative.startsWith("/")) relative = `/${relative}`;

  // 5) concat propre base + relative
  return `${BASE_FILES_URL}${relative}`;
};

/**
 * ✅ FIX: Converts HTTP URLs to HTTPS to avoid Mixed Content warnings
 * when frontend runs on HTTPS but backend serves HTTP URLs
 */
export const ensureHttpsAvatar = (avatarUrl) => {
  if (!avatarUrl) return null;
  
  // If frontend is HTTPS but avatar URL is HTTP, convert it
  if (window.location.protocol === "https:" && avatarUrl.startsWith("http://")) {
    return avatarUrl.replace(/^http:/, "https:");
  }
  
  return avatarUrl;
};

/* ============================================================
   🔹 HOOK PRINCIPAL
   ============================================================ */
/**
 * Centralise :
 * - Résolution avatar → URL
 * - Upload avatar
 * - Update profil doctor
 */
export default function useDoctorInformation(user) {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  /* ---------------- AVATAR SRC ---------------- */
  const avatarSrc = useMemo(() => {
    // ✅ on accepte plusieurs champs possibles (BD varie selon endpoints)
    const raw =
      user?.avatar ||
      user?.avatarUrl ||
      user?.photo ||
      user?.profileImage ||
      user?.image ||
      user?.raw?.avatar ||
      user?.raw?.avatarUrl ||
      null;

    return resolveDoctorAvatar(raw);
  }, [user]);

  /* ---------------- UPLOAD AVATAR ---------------- */
  const handleAvatarUpload = useCallback(
    async (file) => {
      if (!user?.id) {
        message.error("Doctor ID missing");
        return false;
      }
      if (!file) return false;

      try {
        setIsSaving(true);

        const formData = new FormData();
        formData.append("avatar", file); // ✅ fieldname backend: avatar

        await dispatch(updateDoctor({ id: user.id, formData })).unwrap();

        // refresh profil (pour récupérer le nouveau path)
        await dispatch(getCurrentDoctor()).unwrap();

        message.success("Profile picture updated ✅");
        return true;
      } catch (err) {
        message.error(err?.message || err || "Avatar upload failed");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, user?.id]
  );

  /* ---------------- UPDATE PROFILE ---------------- */
  const handleSubmit = useCallback(
    async (values) => {
      if (!user?.id) return false;

      try {
        setIsSaving(true);

        const formData = new FormData();
        const sanitizedValues = sanitizeDoctorUpdateValues(values || {});

        Object.entries(sanitizedValues).forEach(([k, v]) => {
          if (v !== undefined && v !== null) formData.append(k, String(v));
        });

        await dispatch(updateDoctor({ id: user.id, formData })).unwrap();
        await dispatch(getCurrentDoctor()).unwrap();

        message.success("Profile updated ✅");
        return true;
      } catch (err) {
        message.error(err?.message || err || "Update failed");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, user?.id]
  );

  return {
    avatarSrc,          // ✅ URL prête à afficher
    handleAvatarUpload, // ✅ upload image
    handleSubmit,       // ✅ update infos
    isSaving,
  };
}